/**
 * Single adapter for readiness badge levels (MDP-628 rename).
 * Stable keys: novice | apprentice | proficient | coach | architect.
 * Legacy API strings (Professional, Master) and the new strings both resolve
 * to the same key; the integer index (0..4) is unchanged.
 */
export const BADGE_LEVEL_KEYS = Object.freeze(["novice", "apprentice", "proficient", "coach", "architect"]);

export const BADGE_LEVEL_LABELS = Object.freeze({
  novice: "Novice",
  apprentice: "Apprentice",
  proficient: "Proficient",
  coach: "Coach",
  architect: "Architect",
});

const LEGACY_LEVEL_ALIASES = Object.freeze({
  professional: "proficient",
  master: "architect",
});

/** Resolve any legacy/new level string (any case) or integer index to a stable key, else null. */
export function normalizeBadgeLevel(value) {
  if (typeof value === "number") {
    return BADGE_LEVEL_KEYS[value] || null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const lowered = value.trim().toLowerCase();
  if (BADGE_LEVEL_KEYS.includes(lowered)) {
    return lowered;
  }
  return LEGACY_LEVEL_ALIASES[lowered] || null;
}

/** Stable key from an API payload: badge_level_key wins over the legacy badge_level string. */
export function normalizeBadgeLevelFromApi(payload) {
  return normalizeBadgeLevel(payload?.badge_level_key) || normalizeBadgeLevel(payload?.badge_level);
}

/** Display label (e.g. "Proficient") for any legacy/new level value; undefined when unknown. */
export function getBadgeLevelDisplayLabel(value) {
  return BADGE_LEVEL_LABELS[normalizeBadgeLevel(value)];
}

/** Level validator accepting stable keys, new labels and legacy strings. */
export function isBadgeLevelValue(value) {
  return normalizeBadgeLevel(value) !== null;
}

/** True when `level` (legacy or new string) matches any entry of `selectedLevels` (legacy or new strings). */
export function isLevelSelected(selectedLevels, level) {
  const key = normalizeBadgeLevel(level);
  return key !== null && (selectedLevels || []).some((selected) => normalizeBadgeLevel(selected) === key);
}

const WIRE_LEVEL_VALUES = Object.freeze({
  proficient: "professional",
  architect: "master",
});

/** Backend `proficiency_level` query value (apprentice|professional|coach|master) for any legacy/new level string. */
export function toProficiencyLevelWireValue(value) {
  const key = normalizeBadgeLevel(value);
  if (key === null) {
    return String(value).toLowerCase();
  }
  return WIRE_LEVEL_VALUES[key] || key;
}

/** Badge Holders level filter index (1..4) for a pill/filter string in legacy or new naming; null for non-level values. */
export function toBadgeLevelFilterNumber(value) {
  if (typeof value !== "string") {
    return null;
  }
  const index = BADGE_LEVEL_KEYS.indexOf(normalizeBadgeLevel(value));
  return index > 0 ? index : null;
}
